// supervisor-pre-dispatch-checks.ts
//
// Pha kiểm tra TRƯỚC khi dispatch một ticket, tách khỏi supervisor-loop.ts
// (275 dòng, quá ngân sách 200). Trả `undefined` khi đường thông; trả một
// `SupervisorTickResult` khi tick phải dừng ngay tại đây.
//
// Thứ tự trong file này là hợp đồng, không phải tình cờ: gate chạy TRƯỚC
// recoverOwnedCheckout để một ticket bị gate chặn không để lại side effect
// nào — kể cả một lần dọn worktree.

import type { GitRunner } from "./commit-owner.js";
import { evaluateFeatureDispatchGateGuard } from "./feature-dispatch-gate-guard.js";
import type { G2PlanReadiness } from "./g2-plan-readiness.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import type { SupervisorTickResult } from "./supervisor-loop.js";
import { recoverOwnedCheckout } from "./supervisor-owned-checkout-recovery.js";

export interface PreDispatchChecksInput {
  controlRoot: string;
  repoDir: string;
  gitRunner: GitRunner;
  ticket: Ticket;
  /** H4: this tick's plan readiness (supervisor-run-until-idle.ts). Absent ⇒ G2 not evaluated. */
  g2PlanReadiness?: G2PlanReadiness;
}

export async function runPreDispatchChecks(
  input: PreDispatchChecksInput,
): Promise<SupervisorTickResult | undefined> {
  const ticket = input.ticket;
  // Bug I (P15): G0/G1 authority check — BEFORE recoverOwnedCheckout and
  // before journalAttemptStart, so a gate-blocked feature ticket produces no
  // `attempt_started` record and no side effect of any kind. This is the
  // only point on the dispatch path that knows WHICH ticket is about to run,
  // which is why the check lives here rather than in the mission-wide §18
  // `evaluateBeforeDispatch` (see feature-dispatch-gate-guard.ts's header).
  const gateDecision = await evaluateFeatureDispatchGateGuard({
    controlRoot: input.controlRoot,
    // B3: the worktree holding BLUEPRINT.md, re-hashed against G0 per dispatch.
    repoDir: input.repoDir,
    ticketKind: ticket.kind,
    // P20: id + write scope let the guard recognise the controller's own
    // read-only review session, which is exempt from G2 (and only G2).
    ticketId: ticket.id,
    allowedPaths: ticket.allowed_paths,
    g2PlanReadiness: input.g2PlanReadiness,
  });
  if (gateDecision.blocked) {
    return {
      kind: "gate_blocked",
      ticketId: ticket.id,
      code: gateDecision.code ?? "gate_not_approved",
      reason: gateDecision.reason ?? "gate chưa duyệt.",
    };
  }

  // recover_owned_checkout (architecture §6.1): a PREVIOUS attempt on this
  // (or any) ticket may have left rejected/uncommitted changes in the
  // mission's own worktree — never dispatch a new attempt on top of that,
  // or the new attempt's baseline would silently absorb the rejected diff
  // as "already there". Scoped to the mission worktree only, never the
  // human's source checkout (repoDir IS the isolated worktree).
  await recoverOwnedCheckout(input.gitRunner, input.repoDir);

  return undefined;
}
